import {Sidebar} from 'lucide-react';
import * as React from 'react';
import {Routes, Route, Navigate, useLocation, useParams} from 'react-router-dom';
import {AccountDetail} from 'src/account/AccountDetail';
import {AccountOverviewView} from 'src/account/AccountOverviewView';
import {AccountReconcileView} from 'src/account/AccountReconcileView';
import {AccountTransactionsView} from 'src/account/AccountTransactionsView';
import {InvestmentSnapshotsView} from 'src/account/InvestmentSnapshotsView';
import {AccountsListView} from 'src/AccountsView';
import {AnalyticsPage} from 'src/AnalyticsPage';
import {BacklogPage} from 'src/BacklogPage';
import {BalanceAnchorDetailPage} from 'src/BalanceAnchorDetailPage';
import {BenefitsOverviewView} from 'src/BenefitsOverviewView';
import {BottomBar, BottomBarSlotProvider} from 'src/BottomBar';
import {Breadcrumb, BreadcrumbProvider} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {CardBenefitsView} from 'src/CardBenefitsView';
import {CategoriesView, CategoryDetailView} from 'src/CategoriesView';
import {HeaderActionsProvider} from 'src/components/HeaderActions';
import {ConnectionsView, PlaidDebugView} from 'src/ConnectionsView';
import {DefaultsView} from 'src/DefaultsView';
import {EquityView} from 'src/EquityView';
import {FieldsView} from 'src/FieldsView';
import {InboxPage} from 'src/InboxPage';
import {
  InvestmentAccountsListView,
  InvestmentHoldingsView,
  InvestmentPerformanceView,
  InvestmentReconcileView,
  InvestmentsPage,
} from 'src/InvestmentAccountsView';
import {LedgerView} from 'src/LedgerView';
import {MoreSheet} from 'src/MoreSheet';
import {NetWorthView} from 'src/NetWorthView';
import {PayeeLogosView} from 'src/PayeeLogosView';
import {PayrollView} from 'src/PayrollView/PayrollView';
import {RawTransactionDetailPage} from 'src/RawTransactionDetailPage';
import {SettingsView} from 'src/SettingsView';
import SideNav from 'src/SideNav/SideNav';
import {TransactionDetailPage} from 'src/TransactionDetailPage';
import {UnitsView} from 'src/UnitsView';
import {useIsMobile} from 'src/useIsMobile';

/** Redirect /investments/:id to /accounts/:id */
/**
 * A retired list page's address, sent on to the Ledger with its query
 * string intact (plus `extra`, e.g. the layer), so bookmarks and old links
 * keep their filters.
 */
const LedgerRedirect = ({extra = {}}: {extra?: Record<string, string>}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return <Navigate to={query === '' ? '/ledger' : `/ledger?${query}`} replace />;
};

const InvestmentRedirect = () => {
  const {id} = useParams<{id: string}>();
  return <Navigate to={`/accounts/${id}`} replace />;
};

const PlaidDebugRedirect = () => {
  const {id} = useParams<{id: string}>();
  return <Navigate to={`/connections/${id}/debug`} replace />;
};

const RoutedMainPane = () => {
  const isMobile = useIsMobile();
  const [isNavOpen, setIsNavOpen] = React.useState(!isMobile);
  // A phone's More opens a sheet; the sidebar is desktop's.
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);
  const closeMore = React.useCallback(() => setIsMoreOpen(false), []);
  // The sheet exists only in the phone layout: leaving it (a rotation past
  // the breakpoint) closes the sheet rather than leaving it open to reappear.
  React.useEffect(() => {
    if (!isMobile) setIsMoreOpen(false);
  }, [isMobile]);
  // Pages can portal their action buttons into this slot (via HeaderActions)
  // so they share the breadcrumb's row instead of adding a second header row.
  const [actionsSlot, setActionsSlot] = React.useState<HTMLElement | null>(
    null,
  );
  const [bottomBarSlot, setBottomBarSlot] = React.useState<HTMLElement | null>(
    null,
  );

  return (
    <BreadcrumbProvider>
      <HeaderActionsProvider value={actionsSlot}>
      <BottomBarSlotProvider value={bottomBarSlot}>
        <div className="flex h-full w-full flex-1 flex-col">
          <div className="relative flex flex-1 flex-row overflow-hidden">
            {!isMobile && (
              <SideNav isOpen={isNavOpen} />
            )}
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="border-border bg-background text-foreground flex h-14 items-center gap-1.5 border-b p-2 max-sm:pl-4">
                {/* On a phone the tab bar's More opens the drawer, down where
                    the thumb is. */}
                {!isMobile && (
                  <Button
                    variant="ghost"
                    onClick={() => setIsNavOpen(!isNavOpen)}
                  >
                    <Sidebar />
                  </Button>
                )}
                <Breadcrumb />
                <div
                  ref={setActionsSlot}
                  className="ml-auto flex shrink-0 items-center gap-1.5"
                />
              </div>
              <Routes>
                <Route path="/" element={<InboxPage />} />
                <Route path="/inbox/review" element={<InboxPage />} />
                <Route path="/backlog" element={<BacklogPage />} />
                <Route path="/ledger" element={<LedgerView />} />
                <Route path="/transactions" element={<LedgerRedirect />} />
                <Route
                  path="/activity"
                  element={<LedgerRedirect extra={{layer: 'activity'}} />}
                />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/payroll" element={<PayrollView />} />
                <Route path="/equity" element={<EquityView />} />
                <Route path="/net-worth" element={<NetWorthView />} />
                <Route
                  path="/transactions/:id"
                  element={<TransactionDetailPage />}
                />
                {/* Redirect /transfers to the Ledger (unified transaction model) */}
                <Route path="/transfers" element={<Navigate to="/ledger" replace />} />
                <Route
                  path="/raw-transactions/:id"
                  element={<RawTransactionDetailPage />}
                />
                <Route
                  path="/balance-anchors/:id"
                  element={<BalanceAnchorDetailPage />}
                />
                <Route
                  path="/insights"
                  element={<Navigate to="/analytics?tab=insights" replace />}
                />
                <Route path="/settings" element={<SettingsView />} />
                <Route path="/fields" element={<FieldsView />} />
                <Route path="/units" element={<UnitsView />} />
                <Route path="/defaults" element={<DefaultsView />} />
                <Route path="/payee-logos" element={<PayeeLogosView />} />
                <Route path="/categories" element={<CategoriesView />} />
                <Route
                  path="/categories/:id"
                  element={<CategoryDetailView />}
                />
                <Route path="/accounts" element={<AccountsListView />} />
                {/* Account detail: layout route renders the shared header and
                  tab navigation; each tab is a nested route so existing URLs
                  keep working and remain deep-linkable. */}
                <Route path="/accounts/:id" element={<AccountDetail />}>
                  <Route index element={<AccountOverviewView />} />
                  <Route
                    path="transactions"
                    element={<AccountTransactionsView />}
                  />
                  <Route path="reconcile" element={<AccountReconcileView />} />
                  <Route
                    path="snapshots"
                    element={<InvestmentSnapshotsView />}
                  />
                  <Route path="benefits" element={<CardBenefitsView />} />
                  {/* The Plaid tab became a card on Overview; keep old
                    bookmarks working. */}
                  <Route path="plaid" element={<Navigate to=".." replace />} />
                </Route>
                <Route path="/benefits" element={<BenefitsOverviewView />} />
                {/* Investments: layout route renders the Overview/Performance
                  tab strip; each tab is a nested route so both are
                  deep-linkable. */}
                <Route path="/investments" element={<InvestmentsPage />}>
                  <Route index element={<InvestmentAccountsListView />} />
                  <Route path="holdings" element={<InvestmentHoldingsView />} />
                  <Route path="reconcile" element={<InvestmentReconcileView />} />
                  <Route
                    path="performance"
                    element={<InvestmentPerformanceView />}
                  />
                </Route>
                {/* Redirect old /investments/:id to unified /accounts/:id.
                  Static segments (holdings, performance) outrank this dynamic match. */}
                <Route
                  path="/investments/:id"
                  element={<InvestmentRedirect />}
                />
                <Route path="/connections" element={<ConnectionsView />} />
                <Route
                  path="/connections/:id/debug"
                  element={<PlaidDebugView />}
                />
                {/* The Plaid tokens page merged into /connections; keep old
                  bookmarks working. */}
                <Route
                  path="/plaid-tokens"
                  element={<Navigate to="/connections" replace />}
                />
                <Route
                  path="/plaid-tokens/:id/debug"
                  element={<PlaidDebugRedirect />}
                />
              </Routes>
            </div>
          </div>
          {isMobile && (
            <BottomBar
              onMore={() => setIsMoreOpen(true)}
              slotRef={setBottomBarSlot}
            />
          )}
          {isMobile && <MoreSheet open={isMoreOpen} onClose={closeMore} />}
        </div>
      </BottomBarSlotProvider>
      </HeaderActionsProvider>
    </BreadcrumbProvider>
  );
};

export default RoutedMainPane;
